import { useEffect, useRef, useState } from "react";
import { Info } from "lucide-react";

const HINT_WIDTH = 288;
const VIEWPORT_MARGIN = 8;
const GAP_BELOW_ICON = 6;

type HintPosition = { top: number; left: number };

export function InfoHint({ text }: { text: string }) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [position, setPosition] = useState<HintPosition | null>(null);

  const open = () => {
    const icon = buttonRef.current?.getBoundingClientRect();
    if (!icon) return;
    setPosition({
      top: icon.bottom + GAP_BELOW_ICON,
      left: Math.max(
        VIEWPORT_MARGIN,
        Math.min(
          icon.left - HINT_WIDTH / 2 + icon.width / 2,
          window.innerWidth - HINT_WIDTH - VIEWPORT_MARGIN,
        ),
      ),
    });
  };
  const close = () => setPosition(null);

  useEffect(() => {
    if (!position) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    window.addEventListener("scroll", close, true);
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [position]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-label={text}
        onMouseEnter={open}
        onMouseLeave={close}
        onFocus={open}
        onBlur={close}
        onClick={() => (position ? close() : open())}
        className="shrink-0 rounded-full text-gray-400 hover:text-gray-600 focus:text-gray-600 focus:outline-none"
      >
        <Info className="w-3.5 h-3.5" aria-hidden />
      </button>
      {position && (
        <div
          role="tooltip"
          className="pointer-events-none fixed z-[60] rounded-lg bg-gray-900 px-3 py-2 text-xs font-normal leading-relaxed text-white shadow-lg"
          style={{ top: position.top, left: position.left, width: HINT_WIDTH }}
        >
          {text}
        </div>
      )}
    </>
  );
}
